import { useEffect, useState } from 'react';
import { ThemeProvider as ThemeProviderInstance } from 'styled-components';

import { ENV_MEDIA_DOMAIN } from '@lib/core/service/consts';
import {
  COLOR_PRIMARY,
  COLOR_PRODUCT,
  COLOR_SECONDARY,
  ROOT_ELEMENT_ID,
  ROOT_ELEMENT_VARIANT,
} from '@lib/tools/shared/helpers/consts';
import { TColorShades, TFontConfig } from '@lib/tools/shared/helpers/interfaces';
import { useRetailerCustomStyles } from '@lib/tools/views/hooks/useRetailerCustomStyles';

import { MEDIA } from '@components/web/src/styles';

export const ThemeProvider = ({ children }) => {
  const { retailerProductColor, retailerSecondaryColor, retailerPrimaryColor, retailerFonts } =
    useRetailerCustomStyles();
  const [isKioskRootElementType, setIsKioskRootElementType] = useState(false);

  const injectRetailerColorVars = ({
    primary,
    secondary,
    product,
  }: {
    primary: TColorShades;
    secondary: TColorShades;
    product: TColorShades;
  }) => {
    Object.keys(primary).forEach(shade => {
      const primaryVarName = `${COLOR_PRIMARY}${shade}`;
      const secondaryVarName = `${COLOR_SECONDARY}${shade}`;
      const productVarName = `${COLOR_PRODUCT}${shade}`;

      document.documentElement.style.setProperty(primaryVarName, primary[shade]);
      document.documentElement.style.setProperty(secondaryVarName, secondary[shade]);
      document.documentElement.style.setProperty(productVarName, product[shade]);
    });
  };

  const injectRetailerFonts = (fonts: TFontConfig) => {
    if (!fonts) return;

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-retailer-fonts', 'true');

    const fontFaces = Object.values(fonts)
      .map(font =>
        Object.entries(font.weights)
          .map(
            ([weight, path]) => `
          @font-face {
            font-family: '${font.family}';
            font-weight: ${weight};
            src: url('${ENV_MEDIA_DOMAIN}${path}') format('truetype');
            font-display: swap;
          }
        `,
          )
          .join('\n'),
      )
      .join('\n');

    styleEl.innerHTML = fontFaces;
    document.head.appendChild(styleEl);
  };

  useEffect(() => {
    setIsKioskRootElementType(document.getElementById(ROOT_ELEMENT_ID)?.dataset.variant === ROOT_ELEMENT_VARIANT.KIOSK);

    /**
     * Applies CSS variables for theme colors to the <html> element,
     * based on the retailer’s configuration or custom primary/secondary/product colors.
     *
     * For each shade (e.g. '100', '500', '900'), it sets:
     *   --color-primary-{shade}: from primaryColors
     *   --color-secondary-{shade}: from secondaryColors
     *   --color-product-{shade}: from productColors
     *
     * Example:
     * <html style="
     *   --color-primary-100: #B9B8B4;
     *   --color-secondary-100: #F6EEC7;
     *   --color-product-100: #F5BC7B;
     * "><html>
     */
    injectRetailerColorVars({
      primary: retailerPrimaryColor,
      product: retailerProductColor,
      secondary: retailerSecondaryColor,
    });

    injectRetailerFonts(retailerFonts);
  }, [retailerPrimaryColor, retailerSecondaryColor, retailerProductColor, retailerFonts]);

  const theme = {
    breakpoints: { ...MEDIA },
    isKioskRootElementType,
    retailerFonts,
  };
  return <ThemeProviderInstance theme={theme}>{children}</ThemeProviderInstance>;
};
